import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Zap, Gift, MapPin, Bike, Package, Wallet, StickyNote, Phone,
  Calendar, TrendingUp, Shield, IndianRupee, Building2, Pencil, ArrowLeftRight, History, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { inr, inrCompact, num, fmtDate, initials } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { ChartCard, AreaTrend } from "@/components/Charts";
import { ActionModal } from "@/components/ActionModal";
import { EditDailyOrderModal } from "@/components/EditDailyOrderModal";
import { PaymentWithdrawal } from "@/components/PaymentWithdrawal";
import { EarningPlan } from "@/components/EarningPlan";
import { TransferFranchiseModal } from "@/components/TransferFranchiseModal";
import { DPWelcomeKitDialog } from "@/components/DPWelcomeKitDialog";
import { WeeklySummaryDialog } from "@/components/WeeklySummaryDialog";
import { PartnerCertificateCard } from "@/components/certs/PartnerCertificateCard";
import { RainSummaryCard } from "@/components/RainSummaryCard";
import { useApp } from "@/context/AppContext";

const TABS = ["Overview", "Daily Earnings", "Orders", "Incentives", "EV Rental", "Assets", "KYC", "Attendance", "Earning Plan", "Payments", "Payment Withdrawal", "Documents", "Franchise History", "History"];

export default function DPProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { openQuickEntry, role, can } = useApp();
  const canEdit = can("daily.edit");
  const canTransfer = can("dp.transfer");
  const [kitOpen, setKitOpen] = useState(false);
  const [weekOpen, setWeekOpen] = useState(false);
  const [dp, setDp] = useState(null);
  const [daily, setDaily] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [evPay, setEvPay] = useState([]);
  const [assets, setAssets] = useState([]);
  const [att, setAtt] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [payout, setPayout] = useState(null);
  const [modal, setModal] = useState(null);
  const [editRec, setEditRec] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [frHist, setFrHist] = useState(null);

  const load = useCallback(() => {
    api.get(`/dp/${id}`).then(({ data }) => setDp(data));
    api.get(`/dp/${id}/franchise-history`).then(({ data }) => setFrHist(data)).catch(() => {});
    api.get(`/daily/${id}`).then(({ data }) => setDaily(data));
    api.get(`/incentives?dp_id=${id}`).then(({ data }) => setIncentives(data));
    api.get(`/ev/payments?dp_id=${id}`).then(({ data }) => setEvPay(data));
    api.get(`/assets?dp_id=${id}`).then(({ data }) => setAssets(data));
    api.get(`/attendance/${id}`).then(({ data }) => setAtt(data));
    api.get(`/performance/monthly/${id}`).then(({ data }) => setMonthly(data));
    api.get(`/payout/${id}`).then(({ data }) => setPayout(data));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ct-refresh", h);
    return () => window.removeEventListener("ct-refresh", h);
  }, [load]);

  if (!dp) return <div className="animate-pulse text-muted-foreground">Loading profile…</div>;
  const s = dp.stats || {};
  const trend = [...daily].reverse().slice(-20).map((d) => ({ date: d.date_key?.slice(5), earnings: d.total_earnings, orders: d.orders }));

  return (
    <div>
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="dp-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>

      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 font-head text-2xl font-bold text-white">{initials(dp.full_name)}</div>
            <div>
              <div className="flex items-center gap-3"><h1 className="font-head text-2xl font-bold">{dp.full_name}</h1><StatusBadge status={dp.status} /></div>
              <p className="mt-1 text-sm text-muted-foreground font-mono">{dp.fe_id} · {dp.dp_id}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground"><span className="flex items-center gap-1"><Phone className="h-3 w-3" />{dp.mobile}</span><span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{dp.location}, {dp.hub}</span><span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined {fmtDate(dp.joining_date)}</span>
                {dp.franchise_partner_id && <button onClick={() => nav(`/franchise/${dp.franchise_partner_id}`)} data-testid="dp-franchise-chip" className="flex items-center gap-1 rounded-full bg-violet-500/15 px-2 py-0.5 text-violet-500 hover:bg-violet-500/25"><Building2 className="h-3 w-3" />{dp.franchise_partner_name} · {dp.franchise_referral}</button>}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canTransfer && (
              <Button variant="outline" onClick={() => setTransferOpen(true)} className="h-11 active:scale-95" data-testid="dp-transfer-franchise-btn">
                <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Transfer Franchise
              </Button>
            )}
            {can("dp.earnings") && <Button variant="outline" onClick={() => setWeekOpen(true)} className="h-11 active:scale-95" data-testid="dp-weekly-summary-btn"><MessageCircle className="mr-1.5 h-4 w-4" /> Weekly Summary</Button>}
            <Button variant="outline" onClick={() => setKitOpen(true)} className="h-11 active:scale-95" data-testid="dp-welcome-kit-btn"><Gift className="mr-1.5 h-4 w-4" /> Welcome Kit</Button>
            {can("daily.add") && <Button onClick={() => openQuickEntry(dp)} className="h-11 active:scale-95" data-testid="dp-add-daily-btn"><Zap className="mr-1.5 h-4 w-4" /> Add Daily Orders</Button>}
          </div>
        </div>

        {/* Performance strip */}
        <div className="mt-5 grid grid-cols-3 gap-3">
          <Stat label="Today" orders={s.today_orders} earn={s.today_earnings} />
          <Stat label="This Week" orders={s.week_orders} earn={s.week_earnings} />
          <Stat label="This Month" orders={s.month_orders} earn={s.month_earnings} />
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Qa icon={Gift} label="Add Incentive" onClick={() => setModal("incentive")} tid="qa-incentive" />
          <Qa icon={Bike} label="Record EV Payment" onClick={() => setModal("ev-payment")} tid="qa-ev-payment" />
          <Qa icon={Package} label="Issue Asset" onClick={() => setModal("asset")} tid="qa-asset" />
          <Qa icon={Wallet} label="Record Payment" onClick={() => setModal("payment")} tid="qa-payment" />
          <Qa icon={StickyNote} label="Add Note" onClick={() => setModal("note")} tid="qa-note" />
        </div>
      </div>
      <PartnerCertificateCard kind="dp" partnerId={dp.id} />

      {/* Tabs */}
      <Tabs defaultValue="Overview" className="mt-6">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`dp-profile-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-lg border border-transparent px-3 py-1.5 text-xs data-[state=active]:border-border data-[state=active]:bg-card data-[state=active]:shadow-sm">{t}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="Overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <ChartCard title="Daily Earnings Trend" subtitle="Last 20 records" className="lg:col-span-2"><AreaTrend data={trend} x="date" y="earnings" color="#10B981" /></ChartCard>
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-head text-sm font-semibold mb-3">Monthly Summary</h3>
              {monthly && <div className="space-y-2 text-sm">
                <KV k="Total Orders" v={num(monthly.total_orders)} />
                <KV k="Avg Orders/Day" v={monthly.avg_daily_orders} />
                <KV k="Total Earnings" v={inr(monthly.total_earnings)} />
                <KV k="Active Days" v={`${monthly.active_days} (${monthly.attendance_pct}%)`} />
                <KV k="Total Distance" v={`${monthly.total_distance} KM`} />
                <KV k="EV Rental" v={inr(monthly.ev_rental)} />
                <KV k="Net Payable" v={inr(monthly.net_payable)} strong />
              </div>}
            </div>
          </div>
          {dp.ev && <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-head text-sm font-semibold mb-3 flex items-center gap-2"><Bike className="h-4 w-4 text-cyan-500" /> EV Assignment</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
              <KV k="EV ID" v={dp.ev.ev_id} /><KV k="Model" v={dp.ev.model} /><KV k="Plan" v={`${inr(dp.ev.rental_amount)}/${dp.ev.frequency}`} /><KV k="Outstanding" v={inr(dp.ev.outstanding || 0)} strong />
            </div>
          </div>}
        </TabsContent>

        <TabsContent value="Daily Earnings" className="mt-4">
          <RainSummaryCard dpId={dp.id} />
          <DailyTable daily={daily} showCalc onEdit={canEdit ? setEditRec : null} />
        </TabsContent>
        <TabsContent value="Orders" className="mt-4"><DailyTable daily={daily} onEdit={canEdit ? setEditRec : null} /></TabsContent>

        <TabsContent value="Incentives" className="mt-4">
          <SimpleTable head={["Date", "Incentive", "Type", "Amount", "Status"]} rows={incentives.map((i) => [fmtDate(i.date), i.name, i.type, inr(i.amount), <StatusBadge key="s" status={i.status} />])} empty="No incentives" />
        </TabsContent>

        <TabsContent value="EV Rental" className="mt-4">
          {dp.ev ? <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <MiniStat label="Rental" v={`${inr(dp.ev.rental_amount)}/${dp.ev.frequency}`} />
            <MiniStat label="Security Deposit" v={inr(dp.ev.security_deposit)} />
            <MiniStat label="Outstanding" v={inr(dp.ev.outstanding || 0)} accent="rose" />
            <MiniStat label="Total Paid" v={inr(evPay.filter((p) => p.status === "Paid").reduce((a, b) => a + b.amount, 0))} accent="emerald" />
          </div> : <p className="mb-3 text-sm text-muted-foreground">No EV assigned.</p>}
          <SimpleTable head={["Date", "Amount", "Method", "Reference", "Status"]} rows={evPay.map((p) => [fmtDate(p.date), inr(p.amount), p.method, <span key="r" className="font-mono text-xs">{p.reference}</span>, <StatusBadge key="s" status={p.status} />])} empty="No EV payments" />
        </TabsContent>

        <TabsContent value="Assets" className="mt-4">
          <SimpleTable head={["Asset", "Qty", "Issue Date", "Condition", "Return Req.", "Status"]} rows={assets.map((a) => [a.asset, a.quantity, fmtDate(a.issue_date), a.condition, a.return_required ? "Yes" : "No", <StatusBadge key="s" status={a.status} />])} empty="No assets issued" />
        </TabsContent>

        <TabsContent value="KYC" className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-head text-sm font-semibold mb-3 flex items-center gap-2"><Shield className="h-4 w-4 text-emerald-500" /> KYC Details <StatusBadge status={dp.kyc_status} /></h3>
              <div className="space-y-2 text-sm">
                <KV k="Aadhaar" v={dp.aadhaar} /><KV k="PAN" v={dp.pan} /><KV k="Driving Licence" v={dp.dl} />
                <KV k="Bank Account" v={dp.bank_account} /><KV k="IFSC" v={dp.ifsc} /><KV k="Account Holder" v={dp.account_holder} />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Sensitive numbers are masked for privacy.</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-head text-sm font-semibold mb-3">Personal & Emergency</h3>
              <div className="space-y-2 text-sm">
                <KV k="Father/Mother" v={dp.parent_name} /><KV k="Gender" v={dp.gender} /><KV k="DOB" v={fmtDate(dp.dob)} />
                <KV k="Address" v={`${dp.address}, ${dp.city}, ${dp.state} ${dp.pincode}`} />
                <KV k="Emergency" v={`${dp.emergency_name} (${dp.emergency_relation})`} /><KV k="Emergency Phone" v={dp.emergency_phone} />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="Attendance" className="mt-4">
          {att && <div className="mb-4 flex gap-3">
            <MiniStat label="Active Days" v={att.active_days} accent="emerald" />
            <MiniStat label="Inactive Days" v={att.inactive_days} accent="rose" />
          </div>}
          <SimpleTable head={["Date", "Status", "Orders", "Hours", "Login", "Logout"]} rows={(att?.records || []).map((r) => [fmtDate(r.date), r.active ? <StatusBadge key="s" status="Active" /> : <StatusBadge key="s" status="Inactive" />, r.orders, r.working_hours, r.login_time || "-", r.logout_time || "-"])} empty="No attendance" />
        </TabsContent>

        <TabsContent value="Payments" className="mt-4">
          {payout && <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-head text-sm font-semibold mb-3">Month-to-date Payout Statement</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <PayoutBlock title="Gross Earnings" accent="emerald" rows={[["Base Order Earnings", payout.base_earnings], ["Rain Charges", payout.rain_charges], ["Extra Distance", payout.extra_distance], ["Other Incentives", payout.other_incentives], ["Campaign Incentives", payout.campaign_incentives]]} total={payout.gross} />
              <PayoutBlock title="Deductions" accent="rose" rows={[["EV Rent", payout.ev_rent], ["Other Recovery", payout.other_recovery]]} total={payout.ev_rent + payout.other_recovery} />
              <div className="flex flex-col justify-center rounded-lg border border-primary/30 bg-primary/5 p-4 text-center">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Net Payable</p>
                <p className="mt-1 font-head text-3xl font-bold tabular text-primary" data-testid="payout-net">{inr(payout.net_payable)}</p>
                <p className="mt-1 text-xs text-muted-foreground">{num(payout.total_orders)} orders this month</p>
              </div>
            </div>
          </div>}
        </TabsContent>

        <TabsContent value="Earning Plan" className="mt-4">
          <EarningPlan dpId={id} dp={dp} onChanged={load} />
        </TabsContent>

        <TabsContent value="Payment Withdrawal" className="mt-4">
          <PaymentWithdrawal dpId={id} />
        </TabsContent>

        <TabsContent value="Documents" className="mt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Aadhaar", dp.aadhaar], ["PAN", dp.pan], ["Driving Licence", dp.dl], ["Bank Proof", dp.bank_account]].map(([k, v]) => (
              <div key={k} className="rounded-xl border border-border bg-card p-4 text-center">
                <Shield className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-2 text-sm font-medium">{k}</p>
                <p className="text-xs text-muted-foreground font-mono">{v || "—"}</p>
                <StatusBadge status={dp.kyc_status} className="mt-2" />
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Document files are stored securely; only masked numbers are shown here.</p>
        </TabsContent>

        <TabsContent value="Franchise History" className="mt-4 space-y-4">
          {frHist && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Current Franchise</p>
                  <p className="mt-1 font-head text-lg font-bold text-emerald-500" data-testid="fhist-current">{frHist.current.franchise_name || "Unassigned"}</p>
                  <p className="font-mono text-xs text-muted-foreground">{frHist.current.franchise_referral}</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Previous Franchise</p>
                  <p className="mt-1 font-head text-lg font-bold" data-testid="fhist-previous">{frHist.current.previous_franchise_name || "—"}</p>
                </div>
                <div className="rounded-lg border border-border bg-card p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Transfer Effective Date</p>
                  <p className="mt-1 font-head text-lg font-bold" data-testid="fhist-effective">{frHist.current.transfer_effective_date ? fmtDate(frHist.current.transfer_effective_date) : "—"}</p>
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-head text-sm font-semibold">Franchise Assignment History</h3>
                  <Button size="sm" variant="outline" onClick={() => nav(`/transfers?q=${encodeURIComponent(dp.fe_id)}`)} data-testid="dp-view-transfer-history-btn">
                    <History className="mr-1.5 h-3.5 w-3.5" /> View Transfer History
                  </Button>
                </div>
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                  <Table>
                    <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Franchise</TableHead><TableHead>Start Date</TableHead><TableHead>End Date</TableHead><TableHead>Reason</TableHead><TableHead>Transferred By</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {frHist.assignments.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No franchise assignments recorded</TableCell></TableRow>}
                      {frHist.assignments.map((a) => (
                        <TableRow key={a.id} data-testid={`fhist-row-${a.id}`}>
                          <TableCell className="font-semibold">{a.franchise_name} <span className="font-mono text-xs text-muted-foreground">{a.franchise_referral}</span></TableCell>
                          <TableCell>{fmtDate(a.start_date)}</TableCell>
                          <TableCell>{!a.end_date ? "Current" : a.end_date < a.start_date ? "— (0 days)" : fmtDate(a.end_date)}</TableCell>
                          <TableCell className="text-xs">{a.reason}</TableCell>
                          <TableCell className="text-xs">{a.transferred_by}</TableCell>
                          <TableCell><span className={`rounded px-2 py-0.5 text-[10px] font-semibold ${a.status === "Active" ? "bg-emerald-500/15 text-emerald-500" : "bg-secondary text-muted-foreground"}`} data-testid={`fhist-status-${a.id}`}>{a.status === "Active" ? "Active" : "Previous"}</span></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="History" className="mt-4">
          <DailyTable daily={daily} showCalc onEdit={canEdit ? setEditRec : null} />
        </TabsContent>
      </Tabs>

      {modal && <ActionModal kind={modal} dp={dp} open onClose={() => setModal(null)} onSaved={load} />}
      <EditDailyOrderModal record={editRec} onClose={() => setEditRec(null)} onSaved={load} />
      <TransferFranchiseModal dp={dp} open={transferOpen} onClose={() => setTransferOpen(false)} onSaved={load} />
      <DPWelcomeKitDialog dp={kitOpen ? dp : null} onClose={() => setKitOpen(false)} />
      <WeeklySummaryDialog dp={dp} daily={daily} open={weekOpen} onClose={() => setWeekOpen(false)} />
    </div>
  );
}

const Stat = ({ label, orders, earn }) => (
  <div className="rounded-lg border border-border bg-secondary/40 p-3">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 font-head text-lg font-bold tabular">{num(orders)} <span className="text-xs font-normal text-muted-foreground">orders</span></p>
    <p className="text-sm font-semibold tabular text-emerald-500">{inr(earn)}</p>
  </div>
);
const Qa = ({ icon: Icon, label, onClick, tid }) => (
  <Button variant="outline" size="sm" onClick={onClick} data-testid={tid} className="active:scale-95"><Icon className="mr-1.5 h-3.5 w-3.5" />{label}</Button>
);
const KV = ({ k, v, strong }) => (
  <div className="flex items-center justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className={`tabular text-right ${strong ? "font-bold text-primary" : "font-medium"}`}>{v}</span></div>
);
const MiniStat = ({ label, v, accent }) => (
  <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className={`mt-1 font-head text-lg font-bold tabular ${accent === "emerald" ? "text-emerald-500" : accent === "rose" ? "text-rose-500" : ""}`}>{v}</p></div>
);
const PayoutBlock = ({ title, rows, total, accent }) => (
  <div className="rounded-lg border border-border bg-secondary/40 p-4">
    <p className={`text-[11px] font-semibold uppercase tracking-wider ${accent === "rose" ? "text-rose-500" : "text-emerald-500"}`}>{title}</p>
    <div className="mt-2 space-y-1.5 text-sm">{rows.map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="tabular">{inr(v)}</span></div>)}</div>
    <div className="mt-2 flex justify-between border-t border-border pt-2 text-sm font-bold"><span>Total</span><span className="tabular">{inr(total)}</span></div>
  </div>
);

function DailyTable({ daily, showCalc, onEdit }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">
          <TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Normal</TableHead><TableHead className="text-right">Multi</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Multi Earn</TableHead><TableHead className="text-right">Penalties</TableHead>
          <TableHead className="text-right">Base</TableHead>{showCalc && <><TableHead className="text-right">Rain</TableHead><TableHead className="text-right">Distance</TableHead></>}
          <TableHead className="text-right">Total</TableHead>{onEdit && <TableHead className="text-right">Edit</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {daily.length === 0 && <TableRow><TableCell colSpan={onEdit ? 12 : 11} className="text-center text-muted-foreground">No records</TableCell></TableRow>}
          {daily.map((d) => (
            <TableRow key={d.id} data-testid={`daily-row-${d.date_key}`}>
              <TableCell>{fmtDate(d.date)}</TableCell>
              <TableCell className="text-right tabular font-semibold">{d.orders}</TableCell>
              <TableCell className="text-right tabular" data-testid={`daily-normal-${d.date_key}`}>{d.orders - (d.multi_orders || 0)}</TableCell>
              <TableCell className="text-right tabular text-sky-500" data-testid={`daily-multi-${d.date_key}`}>{d.multi_orders || 0}</TableCell>
              <TableCell className="text-right tabular">{inr(d.breakdown.rate)}</TableCell>
              <TableCell className="text-right tabular">{inr(d.multi_earnings || 0)}</TableCell>
              <TableCell className="text-right tabular text-rose-500" data-testid={`daily-penalty-${d.date_key}`} title={`Denial ${d.denial_orders || 0} · Cancelled ${d.cancelled_orders || 0}`}>{d.total_penalties ? `− ${inr(d.total_penalties)}` : "—"}</TableCell>
              <TableCell className="text-right tabular">{inr(d.breakdown.base_earnings)}</TableCell>
              {showCalc && <><TableCell className="text-right tabular" data-testid={`daily-rain-${d.date_key}`} title={d.breakdown.rain_legacy_flat > 0 || (!("rain_orders" in d.breakdown) && d.breakdown.rain_charges > 0) ? "Old flat rain charge" : `${d.breakdown.rain_orders || 0} × ₹${d.breakdown.rain_rate_applied ?? 0}`}>{inr(d.breakdown.rain_charges)}{d.breakdown.rain_orders > 0 && <span className="ml-1 text-[10px] text-sky-500">({d.breakdown.rain_orders})</span>}</TableCell><TableCell className="text-right tabular">{inr(d.breakdown.extra_distance_payout)}</TableCell></>}
              <TableCell className="text-right tabular font-bold text-emerald-500">{inr(d.total_earnings)}</TableCell>
              {onEdit && (
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" data-testid={`edit-daily-${d.date_key}`} onClick={() => onEdit(d)} className="h-8 w-8 p-0">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SimpleTable({ head, rows, empty }) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">{head.map((h) => <TableHead key={h} className={h === "Amount" ? "text-right" : ""}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={head.length} className="text-center text-muted-foreground">{empty}</TableCell></TableRow>}
          {rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j} className={head[j] === "Amount" ? "text-right tabular font-semibold" : ""}>{c}</TableCell>)}</TableRow>)}
        </TableBody>
      </Table>
    </div>
  );
}
